<template>
  <div v-if="filteredPrices.length === 0" class="alert alert-info">
    No price data available for chart.
  </div>
  <div v-else-if="processError" class="alert alert-danger">
    {{ processError }}
  </div>
  <div v-else class="chart-container">
    <div v-if="isProcessing" class="alert alert-info mb-3">
      Loading chart data...
    </div>
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import Chart from "@/chart-setup";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from "vue";

interface EacPrice {
  id: number;
  vintage: string | null;
  bid: string | null;
  ask: string | null;
  source: string;
  name: string;
  date: string;
  created_at: string;
  currency: string | null;
  unit: string | null;
}

interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: (number | null)[];
    borderColor: string;
    backgroundColor: string;
    fill: boolean | string | number;
    tension: number;
    spanGaps?: boolean;
    pointRadius?: number;
    borderWidth?: number;
    currency?: string;
    unit?: string;
  }>;
}

const props = defineProps<{
  filteredPrices: EacPrice[];
  selectedRangeStart?: number;
  selectedRangeEnd?: number;
}>();

const emit = defineEmits<{
  (e: "update:chartData", data: { labels: string[]; datasets: any[] }): void;
}>();

const { globalErrorHandler } = useGlobalHandlers();

// State
const chartCanvas = useTemplateRef<HTMLCanvasElement>("chartCanvas");
// PERF: shallowRef, since Chart.js manages its own instance and a deep reactive proxy only adds overhead
const chartInstance = shallowRef<Chart<
  "line",
  (number | null)[],
  string
> | null>(null);
const chartData = ref<ChartData | null>(null);
const isProcessing = ref(false);
const processError = ref<string | null>(null);
const isMounted = ref(false);
const isRendering = ref(false);

/**
 * Get currency symbol from currency code
 */
const getCurrencySymbol = (currency: string | null): string => {
  if (!currency) {
    return "";
  }

  const symbolMap: Record<string, string> = {
    eur: "€",
    usd: "$",
    gbp: "£",
    jpy: "¥",
    chf: "CHF",
    cad: "C$",
    aud: "A$",
    nzd: "NZ$",
  };

  const lowerCurrency = currency.toLowerCase();
  return symbolMap[lowerCurrency] ?? currency.toUpperCase();
};

/**
 * Detect if filtered prices have mixed currencies or units
 */
const hasMixedCurrencyOrUnit = computed(() => {
  if (props.filteredPrices.length === 0) {
    return false;
  }

  const firstCurrency = props.filteredPrices[0]?.currency?.toLowerCase();
  const firstUnit = props.filteredPrices[0]?.unit;

  return props.filteredPrices.some(
    (price) =>
      price.currency?.toLowerCase() !== firstCurrency ||
      price.unit !== firstUnit
  );
});

/**
 * Process price data into Chart.js format
 * Groups prices by vintage and date for rendering trends
 */
const processChartData = async () => {
  // Don't process if component is unmounting
  if (!isMounted.value) {
    return;
  }

  // Wait for any ongoing render to complete
  while (isRendering.value && isMounted.value) {
    await new Promise((resolve) => setTimeout(resolve, 10));
  }

  if (!isMounted.value) {
    return;
  }

  // IMPORTANT: Destroy old chart FIRST before processing new data
  // This prevents the old chart from receiving animation callbacks while we process
  if (chartInstance.value) {
    await destroyChart();
  }

  if (props.filteredPrices.length === 0) {
    chartData.value = null;
    return;
  }

  isProcessing.value = true;
  processError.value = null;

  try {
    const prices = props.filteredPrices;

    // Group prices by product (name|||source|||currency|||unit) and vintage
    const pricesByProduct: Record<
      string,
      Record<
        string,
        {
          bid: Record<string, number>;
          ask: Record<string, number>;
          singlePrice: Record<string, number>;
          currency: string | null;
          unit: string | null;
        }
      >
    > = {};
    const allDates = new Set<string>();

    prices.forEach((price) => {
      const productKey = `${price.name}|||${price.source}|||${price.currency}|||${price.unit}`;
      const vintageKey = price.vintage !== null ? String(price.vintage) : "N/A";
      const date = price.date.split("T")[0];

      if (!pricesByProduct[productKey]) {
        pricesByProduct[productKey] = {};
      }
      if (!pricesByProduct[productKey][vintageKey]) {
        pricesByProduct[productKey][vintageKey] = {
          bid: {},
          ask: {},
          singlePrice: {},
          currency: price.currency,
          unit: price.unit,
        };
      }

      const bidValue = price.bid ? parseFloat(price.bid) : null;
      const askValue = price.ask ? parseFloat(price.ask) : null;

      // IMPORTANT: Handle both bid-ask spread and single price
      if (bidValue !== null && Number.isFinite(bidValue)) {
        pricesByProduct[productKey][vintageKey].bid[date] = bidValue;
      }

      if (askValue !== null && Number.isFinite(askValue)) {
        pricesByProduct[productKey][vintageKey].ask[date] = askValue;
      }

      // If only one value exists, treat it as single price
      if (
        (bidValue === null && askValue !== null) ||
        (bidValue !== null && askValue === null)
      ) {
        const singleValue = askValue ?? bidValue;
        if (singleValue !== null) {
          pricesByProduct[productKey][vintageKey].singlePrice[date] =
            singleValue;
        }
      }

      allDates.add(date);
    });

    const sortedDates = Array.from(allDates).sort();
    const fullDatasets: any[] = [];
    const datasets: Array<{
      label: string;
      data: (number | null)[];
      borderColor: string;
      backgroundColor: string;
      fill: boolean | string | number;
      tension: number;
      spanGaps?: boolean;
      pointRadius?: number;
      borderWidth?: number;
      currency?: string;
      unit?: string;
    }> = [];

    const colors = [
      "rgba(255, 99, 132, 1)",
      "rgba(54, 162, 235, 1)",
      "rgba(255, 205, 86, 1)",
      "rgba(75, 192, 192, 1)",
      "rgba(153, 102, 255, 1)",
      "rgba(255, 159, 64, 1)",
      "rgba(199, 199, 199, 1)",
      "rgba(83, 102, 255, 1)",
    ];

    let colorIndex = 0;
    for (const [productKey, vintages] of Object.entries(pricesByProduct)) {
      const [productName, source, currency, unit] = productKey.split("|||");

      for (const [vintage, priceData] of Object.entries(vintages)) {
        const baseColor = colors[colorIndex % colors.length];
        const fillColor = baseColor.replace("1)", "0.2)");

        // Check if this product/vintage has bid-ask spread or single price
        const hasBid = Object.keys(priceData.bid).length > 0;
        const hasAsk = Object.keys(priceData.ask).length > 0;

        if (hasBid && hasAsk) {
          // Bid-ask spread: create two datasets with fill
          const bidDataPoints = sortedDates.map(
            (date) => priceData.bid[date] ?? null
          );
          const askDataPoints = sortedDates.map(
            (date) => priceData.ask[date] ?? null
          );

          const bidColor = baseColor.replace("1)", "0.8)");
          const askColor = baseColor;

          const vintageLabel =
            vintage && vintage !== "N/A" ? ` ${vintage}` : "";

          datasets.push({
            label: `${productName} (${source})${vintageLabel} Bid`,
            data: bidDataPoints,
            borderColor: bidColor,
            backgroundColor: fillColor,
            fill: false,
            tension: 0.1,
            spanGaps: true,
            pointRadius: 2,
            borderWidth: 2,
            currency,
            unit,
          });

          datasets.push({
            label: `${productName} (${source})${vintageLabel} Ask`,
            data: askDataPoints,
            borderColor: askColor,
            backgroundColor: fillColor,
            fill: `-1`,
            tension: 0.1,
            spanGaps: true,
            pointRadius: 2,
            borderWidth: 2,
            currency,
            unit,
          });

          fullDatasets.push({
            label: `${productName} (${source})${vintageLabel} Ask`,
            data: askDataPoints,
            borderColor: askColor,
            backgroundColor: fillColor,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
            borderWidth: 1,
          });
        } else {
          // Single price: create one dataset
          const singleDataPoints = sortedDates.map(
            (date) =>
              priceData.singlePrice[date] ??
              priceData.ask[date] ??
              priceData.bid[date] ??
              null
          );

          const vintageLabel =
            vintage && vintage !== "N/A" ? ` ${vintage}` : "";

          datasets.push({
            label: `${productName} (${source})${vintageLabel}`,
            data: singleDataPoints,
            borderColor: baseColor,
            backgroundColor: fillColor,
            fill: false,
            tension: 0.1,
            spanGaps: true,
            pointRadius: 3,
            borderWidth: 2,
            currency,
            unit,
          });

          fullDatasets.push({
            label: `${productName} (${source})${vintageLabel}`,
            data: singleDataPoints,
            borderColor: baseColor,
            backgroundColor: fillColor,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
            borderWidth: 1,
          });
        }

        colorIndex++;
      }
    }

    // Emit full dataset for range selector
    emit("update:chartData", {
      labels: sortedDates,
      datasets: fullDatasets,
    });

    // Apply range selection if provided
    let displayLabels = sortedDates;
    let displayDatasets = datasets;

    if (
      props.selectedRangeStart !== undefined &&
      props.selectedRangeEnd !== undefined
    ) {
      // Validate and clamp indices to array bounds
      const start = Math.max(
        0,
        Math.min(Math.floor(props.selectedRangeStart), sortedDates.length - 1)
      );
      const end = Math.max(
        0,
        Math.min(Math.floor(props.selectedRangeEnd), sortedDates.length - 1)
      );

      // Ensure end >= start, otherwise use full arrays
      if (end >= start) {
        displayLabels = sortedDates.slice(start, end + 1);
        displayDatasets = datasets.map((dataset) => ({
          ...dataset,
          data: dataset.data.slice(start, end + 1),
        }));
      }
    }

    chartData.value = {
      labels: displayLabels,
      datasets: displayDatasets,
    };

    await nextTick();
    await renderChart();
    isProcessing.value = false;
  } catch (error: any) {
    globalErrorHandler(error);
    processError.value = "Failed to process chart data";
    isProcessing.value = false;
  }
};

/**
 * Render the Chart.js chart with robust error handling
 */
const renderChart = async () => {
  // Prevent concurrent renders
  if (isRendering.value) {
    return;
  }

  isRendering.value = true;

  try {
    // Wait for DOM updates
    await nextTick();

    // Retry mechanism for canvas availability
    let retries = 0;
    const maxRetries = 10;

    while (retries < maxRetries) {
      if (chartCanvas.value && chartData.value) {
        break;
      }

      retries++;
      await new Promise((resolve) => setTimeout(resolve, 50));
      await nextTick();
    }

    if (!chartCanvas.value || !chartData.value) {
      isRendering.value = false;
      return;
    }

    if (!chartCanvas.value.isConnected) {
      isRendering.value = false;
      return;
    }

    const rect = chartCanvas.value.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      isRendering.value = false;
      return;
    }

    const ctx = chartCanvas.value.getContext("2d");
    if (!ctx) {
      isRendering.value = false;
      return;
    }

    // Sanity check: chart should already be destroyed by processChartData
    // But if somehow it still exists, destroy it now
    if (chartInstance.value) {
      await destroyChart();
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Verify canvas is still valid after destruction
      if (!chartCanvas.value || !chartCanvas.value.isConnected) {
        isRendering.value = false;
        return;
      }
    }

    const yAxisTitle = hasMixedCurrencyOrUnit.value
      ? "Price (Mixed)"
      : (() => {
          const firstDataset = chartData.value.datasets[0];
          if (firstDataset?.currency && firstDataset?.unit) {
            const symbol = getCurrencySymbol(firstDataset.currency);
            return `Price (${symbol}/${firstDataset.unit})`;
          }
          return "Price";
        })();

    chartInstance.value = new Chart(ctx, {
      type: "line",
      data: chartData.value,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0,
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        scales: {
          y: {
            beginAtZero: false,
            title: {
              display: true,
              text: yAxisTitle,
            },
            ticks: {
              callback: function (value) {
                if (hasMixedCurrencyOrUnit.value) {
                  return Number(value).toFixed(2);
                }
                const firstDataset = chartData.value?.datasets[0];
                if (firstDataset?.currency) {
                  const symbol = getCurrencySymbol(firstDataset.currency);
                  return symbol + Number(value).toFixed(2);
                }
                return Number(value).toFixed(2);
              },
            },
          },
          x: {
            title: {
              display: true,
              text: "Date",
            },
          },
        },
        plugins: {
          title: {
            display: true,
            text: "EAC Price Trends",
          },
          legend: {
            display: true,
            onClick: (event, legendItem, legend) => {
              const index = legendItem.datasetIndex;
              const chart = legend.chart;
              if (index !== undefined && chart.isDatasetVisible(index)) {
                chart.hide(index);
                legendItem.hidden = true;
              } else if (index !== undefined) {
                chart.show(index);
                legendItem.hidden = false;
              }
              chart.update();
            },
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              title: function (context) {
                return `Date: ${context[0].label}`;
              },
              label: function (context) {
                const label = context.dataset.label || "";
                const value = context.parsed.y;
                const dataset = context.dataset as any;
                const currency = dataset.currency;
                const unit = dataset.unit;

                const isValidCurrency =
                  currency &&
                  currency !== "null" &&
                  currency !== "NULL" &&
                  currency !== "None";
                const isValidUnit =
                  unit && unit !== "null" && unit !== "NULL" && unit !== "None";

                if (!isValidCurrency || !isValidUnit) {
                  return `${label}: ${Number(value).toFixed(4)}`;
                }

                const symbol = getCurrencySymbol(currency);
                return `${label}: ${symbol}${Number(value).toFixed(4)}/${unit}`;
              },
              afterBody: function (context) {
                if (context.length >= 2) {
                  const label1 = context[0].dataset.label || "";
                  const label2 = context[1]?.dataset.label || "";

                  if (label1.includes(" Bid") && label2.includes(" Ask")) {
                    const bid = context[0].parsed.y;
                    const ask = context[1].parsed.y;
                    const dataset = context[0].dataset as any;
                    const currency = dataset.currency;
                    const unit = dataset.unit;

                    const isValidCurrency =
                      currency &&
                      currency !== "null" &&
                      currency !== "NULL" &&
                      currency !== "None";
                    const isValidUnit =
                      unit &&
                      unit !== "null" &&
                      unit !== "NULL" &&
                      unit !== "None";

                    if (
                      bid !== null &&
                      ask !== null &&
                      isValidCurrency &&
                      isValidUnit
                    ) {
                      const spread = ask - bid;
                      const symbol = getCurrencySymbol(currency);
                      return [`Spread: ${symbol}${spread.toFixed(4)}/${unit}`];
                    }
                  }
                }
                return [];
              },
            },
          },
        },
      },
    });
  } catch (error) {
    globalErrorHandler(error);
    processError.value = "Failed to render chart";
  } finally {
    isRendering.value = false;
  }
};

/**
 * Clean up chart instance safely
 * Stops all animations and pending callbacks before destroying
 */
const destroyChart = async () => {
  if (chartInstance.value) {
    try {
      const chart = chartInstance.value;

      // Clear the reference immediately to prevent any new operations
      chartInstance.value = null;

      // Stop all animations and pending render callbacks
      chart.stop();

      // Reset chart to prevent any pending updates
      chart.clear();

      // Wait for multiple animation frames to ensure all pending callbacks complete
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Now safe to destroy
      chart.destroy();
    } catch {
      // Silently handle any errors during destruction
    }
  }
};

/**
 * Clean up all resources
 */
const cleanup = async () => {
  // Mark as no longer mounted first to prevent any new operations
  isMounted.value = false;

  // Destroy chart
  await destroyChart();

  // Reset state
  chartData.value = null;
  isProcessing.value = false;
  processError.value = null;
  isRendering.value = false;
};

// Initialize chart when component is mounted
onMounted(() => {
  isMounted.value = true;
  if (props.filteredPrices.length > 0) {
    processChartData();
  }
});

// Watch for data changes to update chart
watch(
  () => props.filteredPrices,
  () => {
    if (isMounted.value) {
      processChartData();
    }
  },
  { deep: true }
);

// Watch for range changes
watch(
  () => [props.selectedRangeStart, props.selectedRangeEnd],
  () => {
    if (isMounted.value && props.filteredPrices.length > 0) {
      processChartData();
    }
  }
);

// Cleanup when component is destroyed
onBeforeUnmount(() => {
  cleanup();
});
</script>

<style lang="scss" scoped>
/* Chart Container Styling */
.chart-container {
  position: relative;
  height: 400px;
}
</style>
